import classNames from 'classnames/bind';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';

import styles from './common.module.scss';

import actions from '@/actions';
import FieldRow from '@/components/ui/FieldRow';
import Checkbox from '@/components/ui/kit/Checkbox';
import { FieldNumber, FieldText } from '@/components/ui/kit/Field';
import Select from '@/components/ui/kit/Select';
import { SHOOT_INTERVAL } from '@/config';
import { getElevation } from '@/utils/MapUtils';

const cx = classNames.bind(styles);

const CAMERA = {
  ON: 'ON',
  OFF: 'OFF',
};

const Command = ({ index, data: command }) => {
  const store = useStore();
  const dispatch = useDispatch();
  const commands = useSelector((state) => state.mission.json.commands);
  const [camera, setCamera] = useState();
  const domRefs = useRef({
    name: useRef(),
    latitude: useRef(),
    longitude: useRef(),
    elevation: useRef(),
    relAltitude: useRef(),
    aglAltitude: useRef(),
    mslAltitude: useRef(),
    speed: useRef(),
    interval: useRef(),
  }).current;

  // 홈 지면 고도
  const homeElevation = useMemo(() => {
    if (commands.length > 0) {
      return commands[0].data.elevation;
    }
    return 0;
  }, [commands]);

  const { mslAltitude, aglAltitude } = useMemo(() => {
    const mslAltitude = command.data.altitude + homeElevation;
    const aglAltitude = mslAltitude - command.data.elevation;

    return { mslAltitude, aglAltitude };
  }, [homeElevation, command.data]);

  const hasSpeed = useMemo(() => {
    return command.data.speed !== undefined;
  }, [command.data.speed]);

  // 카메라 사용여부
  const hasCamera = useMemo(() => {
    return command.data.camera !== undefined;
  }, [command.data.camera]);

  useEffect(() => {
    domRefs.name.current.value = command.data.name;
    domRefs.latitude.current.value = command.data.position.lat.toFixed(7);
    domRefs.longitude.current.value = command.data.position.lng.toFixed(7);
    domRefs.elevation.current.value = command.data.elevation;
    domRefs.relAltitude.current.value = command.data.altitude.toFixed(1);
    domRefs.aglAltitude.current.value = aglAltitude.toFixed(1);
    domRefs.mslAltitude.current.value = mslAltitude.toFixed(1);
    domRefs.speed.current.value = command.data.speed?.toFixed(1) ?? '';

    if (hasCamera) {
      setCamera(command.data.camera.shoot ? CAMERA.ON : CAMERA.OFF);
      domRefs.interval.current.value = command.data.camera.interval.toFixed(1);
    } else {
      setCamera('None');
    }
  }, [command.data]);

  const updateName = (e) => {
    dispatch(actions.mission.editWaypoint(index, 'name', e.target.value));
  };

  const updatePosition = (position) => {
    getElevation(position).then((elevation) => {
      const waypoint = { position, elevation };
      dispatch(actions.mission.moveWaypoint(command.id, waypoint));
    });
  };

  const handleBlur = (e) => {
    const name = e.target.name;
    const value = Number(e.target.value);

    // Latitude 변경된 경우
    if (name === 'latitude') {
      updatePosition({ lat: value, lng: command.data.position.lng });
    }
    // Longitude 변경된 경우
    else if (name === 'longitude') {
      updatePosition({ lat: command.data.position.lat, lng: value });
    }
    // Relative Altitude 변경된 경우
    else if (name === 'relAltitude') {
      dispatch(actions.mission.editWaypoint(index, 'altitude', value));
    }
    // AGL Altitude 변경된 경우
    else if (name === 'aglAltitude') {
      const altitude = Number((command.data.elevation + value - homeElevation).toFixed(1));
      dispatch(actions.mission.editWaypoint(index, 'altitude', altitude));
    }
    // MSL Altitude 변경된 경우
    else if (name === 'mslAltitude') {
      const altitude = Number((value - homeElevation).toFixed(1));
      dispatch(actions.mission.editWaypoint(index, 'altitude', altitude));
    }
    // Speed 변경된 경우
    else if (name === 'speed') {
      dispatch(actions.mission.editWaypoint(index, 'speed', Math.max(0, value)));
    }
    // Shoot Interval 변경된 경우
    else if (name === 'interval') {
      dispatch(actions.mission.editWaypoint(index, 'camera.interval', Math.max(0, value)));
    }
  };

  const toggleHasSpeed = () => {
    const nextValue = hasSpeed ? undefined : store.getState().editor.config.speed;
    dispatch(actions.mission.editWaypoint(index, 'speed', nextValue));
  };

  const toggleCamera = () => {
    if (hasCamera) {
      setCameraNone();
    } else {
      setCameraOn();
    }
  };

  const selectCamera = (e) => {
    switch (e.target.value) {
      case CAMERA.ON:
        setCameraOn();
        break;

      case CAMERA.OFF:
        setCameraOff();
        break;

      default:
        setCameraNone();
        break;
    }
  };

  const setCameraOn = () => {
    dispatch(actions.mission.editWaypoint(index, 'camera', { shoot: true, interval: SHOOT_INTERVAL }));
  };

  const setCameraOff = () => {
    dispatch(actions.mission.editWaypoint(index, 'camera', { shoot: false, interval: 0 }));
  };

  const setCameraNone = () => {
    dispatch(actions.mission.editWaypoint(index, 'camera', undefined));
  };

  return (
    <div className={cx('container')}>
      <FieldRow label="Name">
        <FieldText ref={domRefs.name} name="name" maxLength={24} onBlur={updateName} />
      </FieldRow>
      <FieldRow label="Latitude">
        <FieldNumber ref={domRefs.latitude} name="latitude" min={-85} max={85} onBlur={handleBlur} />
      </FieldRow>
      <FieldRow label="Longitude">
        <FieldNumber ref={domRefs.longitude} name="longitude" min={-180} max={180} onBlur={handleBlur} />
      </FieldRow>
      <FieldRow label="Elevation" unit="m">
        <FieldNumber ref={domRefs.elevation} name="elevation" readOnly />
      </FieldRow>
      <FieldRow label="Relative Altitude" unit="m">
        <FieldNumber ref={domRefs.relAltitude} name="relAltitude" onBlur={handleBlur} />
      </FieldRow>
      <FieldRow label="AGL Altitude" unit="m">
        <FieldNumber
          ref={domRefs.aglAltitude}
          name="aglAltitude"
          className={cx({ danger: aglAltitude <= 0 })}
          onBlur={handleBlur}
        />
      </FieldRow>
      <FieldRow label="MSL Altitude" unit="m">
        <FieldNumber ref={domRefs.mslAltitude} name="mslAltitude" onBlur={handleBlur} />
      </FieldRow>
      <FieldRow label="Speed" unit="m/s">
        <Checkbox checked={hasSpeed} onClick={toggleHasSpeed} />
        <FieldNumber ref={domRefs.speed} name="speed" disabled={!hasSpeed} onBlur={handleBlur} />
      </FieldRow>
      <FieldRow label="Camera">
        <Checkbox checked={hasCamera} onClick={toggleCamera} />
        <Select value={camera} disabled={!hasCamera} onChange={selectCamera} className={cx('select')}>
          {!hasCamera && <option value="None">None</option>}
          <option value={CAMERA.ON}>On</option>
          <option value={CAMERA.OFF}>Off</option>
        </Select>
      </FieldRow>
      {hasCamera && (
        <FieldRow label="Shoot Interval" unit="m">
          <FieldNumber
            ref={domRefs.interval}
            name="interval"
            readOnly={!command.data.camera.shoot}
            onBlur={handleBlur}
          />
        </FieldRow>
      )}
    </div>
  );
};

export default Command;
